import { useCallback, useState, useRef, useMemo, useEffect } from "react";
import { useHistory } from "react-router-dom";
import { useForm } from "react-hook-form";
import { FormGroup, Label, Progress } from "reactstrap";
import { timeDifference } from "utils/timeDifference";
import { ImageUpload, FormEntry, FormSelect, CustomAlert } from "components";
import useProgressBar from "components/ProgressBar/useProgressBar";
import { encodeImageFileAsURL } from "utils/fileToBase64";
import { removeEmptyStringFields } from "utils/objectCleaning";
import apiService from "services/api";
import { useNotificationContext } from "contexts";
import { daysMapper, leaveTypes, amPmOptions } from "../constants";
import { capitalize } from "utils/capitalize";
import { useQuery } from "react-query";
import moment from "moment";

const isProofRequired = (type) => !["CL", "RO", "AL", "BL"].includes(type);
const isReasonRequired = (type) => ["NPL", "SL", "WFH", "CL"].includes(type);

const dayIndexMap = {
  0: "Monday",
  1: "Tuesday",
  2: "Wednesday",
  3: "Thursday",
  4: "Friday",
  5: "Saturday",
  6: "Sunday",
};

const NewStaffApplicationTemplate = ({ type }) => {
  const { data: staff, isLoading } = useQuery("staff", () =>
    apiService.staff.self(),
  );

  const isLeave = type === "leave";

  const leaveInitialState = {
    date: undefined,
    type: isLeave ? "RO" : undefined,
    period: isLeave ? "HD" : undefined,
    startTime: undefined,
    endTime: undefined,
    reason: undefined,
    proofImg: undefined,
    is_ot: !isLeave,
    amPm: "am",
  };

  const initialConfirmationModalProps = {
    show: false,
    data: leaveInitialState,
  };

  const history = useHistory();
  const [ProgressBar, startProgress, finishProgress] = useProgressBar();
  const fileInputRef = useRef();
  const [showLoading, setShowLoading] = useState(false);
  const [confirmationModalProps, setConfirmationModalProps] = useState({});

  const { createNotification } = useNotificationContext();

  const {
    register,
    handleSubmit,
    watch,
    getValues,
    formState: { errors },
    setError,
    setValue,
    clearErrors = () => {},
  } = useForm({
    defaultValues: leaveInitialState,
    mode: "onChange",
    reValidateMode: "onChange",
  });

  const applicationType = watch("type");

  const onSubmit = async (data) => {
    clearErrors();
    const formData = {
      ...removeEmptyStringFields(data),
    };
    var proofImg = "";
    if (isProofRequired(getValues("type"))) {
      if (!fileInputRef?.current?.files?.length)
        return setError("proofImg", {
          message: "Please input image proof",
        });
    }

    if (fileInputRef.current?.files?.length) {
      const file = fileInputRef?.current?.files[0];
      proofImg = await encodeImageFileAsURL(file);
    }

    setConfirmationModalProps({
      show: true,
      data,
      callback: () => {
        setShowLoading(true);
        apiService.leave
          .create({ ...formData, ...(proofImg ? { proofImg } : {}) })
          .then(() => {
            createNotification({
              type: "success",
              message: "Application successfully submitted",
            });
            history.push(`/${type.toLowerCase()}/me`);
          })
          .catch((error) =>
            createNotification({
              type: "danger",
              message: error.message,
            }),
          )
          .finally(() => setShowLoading(false));
      },
    });
  };

  const hideConfirmationModal = () => {
    setShowLoading(false);
    setConfirmationModalProps(initialConfirmationModalProps);
  };

  useEffect(() => {
    if (!isLeave) return;
    setValue("period", "FD");
  }, [applicationType, setValue, isLeave]);

  return (
    <div className="content">
      <CustomAlert
        show={useMemo(
          () => confirmationModalProps.show,
          [confirmationModalProps.show],
        )}
        hideModal={hideConfirmationModal}
        title="Confirm?"
        submitButtonText="Add"
        body={useMemo(
          () => (
            <div className="text-warning">
              <div>
                {`Apply for ${
                  isLeave
                    ? `${confirmationModalProps?.data?.type} (${confirmationModalProps?.data?.period})`
                    : "ot"
                } on ${moment(confirmationModalProps?.data?.date).format("ddd DD MMM YYYY")}?`}
              </div>
              {isLeave && (
                <div className="mt-3">
                  Leaves can only be deleted more than 2 days before the leave date. Deletion leaves a trace in the database for audit purposes.
                </div>
              )}
            </div>
          ),
          [confirmationModalProps.data],
        )}
        disabled={showLoading}
        submitModal={useCallback(confirmationModalProps.callback, [
          confirmationModalProps.callback,
        ])}
      />
      <form onSubmit={handleSubmit(onSubmit)}>
        <div className="d-flex justify-content-center">
          <div className="w-75">
            <h4 className="mb-4 text-center">{capitalize(type)} Application</h4>
            {isLeave && staff && (
              <div className="card">
                <div className="card-body">
                  <div className="card-description">
                    <div className="row">
                      <div className="col-sm-6">
                        <small className="h6 text-info">
                          Compensation Leaves Available
                        </small>
                      </div>
                      <div className="col-sm-6">
                        <small>{staff.compensationLeaveHoursCount} hours</small>
                      </div>
                      <div className="col-sm-6">
                        <small className="h6 text-info">
                          Sick Leaves Available
                        </small>
                      </div>
                      <div className="col-sm-6">
                        <small>{staff.sickLeaveDaysCount} days</small>
                      </div>
                      <div className="col-sm-6">
                        <small className="h6 text-info">
                          Annual Leaves Available
                        </small>
                      </div>
                      <div className="col-sm-6">
                        <small>{staff.annualLeaveDaysCount} days</small>
                      </div>
                      <div className="col-sm-6">
                        <small className="h6 text-info">
                          Unpaid Leaves Taken
                        </small>
                      </div>
                      <div className="col-sm-6">
                        <small>{staff.unpaidLeaveDaysCount} hours</small>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}
            <FormEntry
              formLabel={`${capitalize(type)} Date`}
              type="date"
              register={register}
              name="date"
              error={errors.date}
              required={true}
              validation={{
                validate: {
                  checkCrazyYear: (leaveDate) => {
                    return (
                      moment(leaveDate) < moment(new Date()).add(1, "year") ||
                      "Cannot apply leave more than 1 year ahead"
                    );
                  },
                },
              }}
            />
            {isLeave && (
              <FormSelect
                formLabel={`${capitalize(type)} Type`}
                register={register}
                name="type"
                options={leaveTypes}
                error={errors.type}
                required={true}
              />
            )}
            {isLeave && (
              <FormSelect
                formLabel={`${capitalize(type)} Period`}
                register={register}
                name="period"
                options={daysMapper[watch("type") || "RO"]}
                error={errors.period}
                required={true}
              />
            )}
            {(watch("period") === "PD" || !isLeave) && (
              <div>
                <FormEntry
                  formLabel={`${capitalize(type)} Start Time`}
                  name="startTime"
                  register={register}
                  type="time"
                  error={errors.startTime}
                  required={true}
                />
                <FormEntry
                  formLabel={`${capitalize(type)} End Time`}
                  register={register}
                  name="endTime"
                  validation={{
                    validate: {
                      checkLessThan: (value) =>
                        timeDifference(getValues("startTime"), value) > 0 ||
                        `${capitalize(type)} Start Time must be before ${capitalize(type)} End Time.`,
                      ...(isLeave
                        ? {
                            checkMaxValue: (value) =>
                              timeDifference(getValues("startTime"), value) <
                                540 || "Select Full Day as your option.",
                          }
                        : {}),
                    },
                  }}
                  type="time"
                  error={errors.endTime}
                />
              </div>
            )}
            {watch("period") === "HD" && isLeave && (
              <div>
                <FormSelect
                  formLabel={`${capitalize(type)} AM/PM`}
                  register={register}
                  name="amPm"
                  options={amPmOptions}
                  error={errors.period}
                  required={true}
                />
              </div>
            )}
            {(isReasonRequired(watch("type")) || !isLeave) && (
              <FormEntry
                formLabel="Reason"
                type="text"
                register={register}
                name="reason"
                error={errors.reason}
                placeholder={
                  watch("type") === "CL"
                    ? "Please state the date where OT is claimed from"
                    : `Reason Of ${capitalize(type)}`
                }
                required={isReasonRequired(watch("type"))}
              />
            )}
            {isProofRequired(watch("type")) && (
              <div className="ml-3 mt-3">
                <FormGroup>
                  <Label for="leaveProof" className="text-white">
                    Proof of Reason
                  </Label>
                </FormGroup>
                <ImageUpload
                  hasButton={false}
                  fileInputRef={fileInputRef}
                  error={errors.proofImg}
                  clearErrors={clearErrors}
                  apiUrl={process.env.REACT_APP_API_URL}
                />
              </div>
            )}
            {showLoading && <ProgressBar />}
            <button
              className={`btn btn-primary float-right ${showLoading && "is-disabled"}`}
              type="submit"
              disabled={showLoading}
            >
              Submit
            </button>
          </div>
        </div>
      </form>
    </div>
  );
};

export default NewStaffApplicationTemplate;
